import type { EventDto } from '@vision360/contracts';
import { lazy } from 'react';
import { z } from 'zod';
import { toDataState, useEvents } from '../_sdk/hooks';
import { defineWidget } from '../_sdk/types';

/**
 * Filterable reverse-chronological stream (PRD VS-042, VS-043): new events
 * arrive through the live stream into the cached list (TechSpec §7.2).
 */

export const PropsSchema = z.object({
  label: z.string().max(80).optional(),
  /** Empty: every source. */
  sourceId: z.uuid().optional(),
  /** e.g. `erp.` — every kind starting with it. */
  kindPrefix: z.string().max(200).optional(),
  minSeverity: z.number().int().min(0).max(4).default(0),
  /** At most 100: longer lists would need virtualization (§8.3). */
  limit: z.number().int().min(5).max(100).default(30),
});

export type EventFeedProps = z.output<typeof PropsSchema>;

export default defineWidget<EventFeedProps, EventDto[]>({
  type: 'event-feed',
  category: 'event',
  defaultSize: { w: 4, h: 5 },
  minSize: { w: 3, h: 3 },
  propsSchema: PropsSchema,
  defaults: { minSeverity: 0, limit: 30 },
  sourceIds: (p) => (p.sourceId ? [p.sourceId] : []),
  useData: (p) =>
    toDataState(
      useEvents({
        sourceId: p.sourceId || undefined,
        kindPrefix: p.kindPrefix || undefined,
        minSeverity: p.minSeverity || undefined,
        limit: p.limit,
      }),
      {
        select: (r) => r.events,
        isEmpty: (events) => events.length === 0,
        lastUpdated: (events) => (events[0] ? new Date(events[0].ts) : undefined),
      },
    ),
  Render: lazy(() => import('./render')),
  Editor: lazy(() => import('./editor')),
});
